import { Signal } from "@engine";
import type { Shape } from "../level/types";

const STORAGE_KEY = "morph.stats.v1";

// Discriminated union of every "thing the player did" that the
// achievement layer wants to count. Keeping a tagged kind on each
// bump means subscribers can be cheap O(1) switches instead of
// re-reading the whole stats blob every time.
export type StatBumpKind =
  | "perfect"
  | "save"
  | "boss"
  | "frenzy"
  | "comboReached"
  | "shapeVisited"
  | "dailyPlayed"
  | "magnet"
  | "double"
  | "runEnded";

export interface StatBumpEvent {
  kind: StatBumpKind;
  // Optional payload for kinds that need it. delta is the amount the
  // primary counter just changed by (defaults to 1 for tick-style
  // events). value is the NEW total or NEW current value, used by
  // achievements that compare a threshold.
  delta?: number;
  value?: number;
  bossId?: string;
  shape?: Shape;
  difficulty?: string;
}

// Persisted lifetime stats. Per-run counters (perfectsThisRun,
// savesThisRun, bossesThisRun, shapesVisitedThisRun, frenziesThisRun,
// comboThisRun, perfectStreakThisRun) reset on each new run via
// beginRun(); the totals on the same blob keep growing across runs.
export interface StatsBlob {
  runs: number;
  distanceTotal: number;
  scoreTotal: number;
  perfectsTotal: number;
  savesTotal: number;
  bossesTotal: number;
  frenziesTotal: number;
  longestCombo: number;
  longestPerfectStreak: number;
  // Set serialised as a string[] -- BossId from bosses.ts. Used by
  // the "clear all four named bosses" achievement.
  bossesById: string[];
  // Set of UTC date keys (YYYY-MM-DD) on which the player has played
  // daily mode. Drives the "daily_7" achievement; deduped at insert.
  dailyKeysPlayed: string[];
  // Per-run counters; persisted alongside the totals so a refresh
  // mid-run doesn't lose progress on the active counters that drive
  // FRENZY_3X / PERFECT_10 / ALL_SHAPES.
  perfectsThisRun: number;
  savesThisRun: number;
  bossesThisRun: number;
  frenziesThisRun: number;
  shapesThisRun: Shape[];
  // Power-pickup counters (magnet + double-score). Per-run drives
  // the "magnetised" achievement; lifetime totals are surfaced in
  // the StatsScreen for player flex.
  magnetsTotal: number;
  doublesTotal: number;
  magnetsThisRun: number;
  doublesThisRun: number;
}

const EMPTY: StatsBlob = {
  runs: 0,
  distanceTotal: 0,
  scoreTotal: 0,
  perfectsTotal: 0,
  savesTotal: 0,
  bossesTotal: 0,
  frenziesTotal: 0,
  longestCombo: 0,
  longestPerfectStreak: 0,
  bossesById: [],
  dailyKeysPlayed: [],
  perfectsThisRun: 0,
  savesThisRun: 0,
  bossesThisRun: 0,
  frenziesThisRun: 0,
  shapesThisRun: [],
  magnetsTotal: 0,
  doublesTotal: 0,
  magnetsThisRun: 0,
  doublesThisRun: 0,
};

// Single source of truth for "what has the player done over time".
// AchievementService subscribes to onStatBumped and re-evaluates
// predicates whenever something interesting changes; the StatsScreen
// reads getAll() on demand.
export class StatsService {
  readonly onStatBumped = new Signal<StatBumpEvent>();
  private blob: StatsBlob = { ...EMPTY };

  constructor() {
    this.blob = this.load();
  }

  // Reset all per-run counters. Called by MorphHexGame.startGame so
  // the run-scoped achievements (PERFECT_10_RUN, FRENZY_3X, etc)
  // start fresh. Lifetime totals stay untouched.
  beginRun(): void {
    this.blob.perfectsThisRun = 0;
    this.blob.savesThisRun = 0;
    this.blob.bossesThisRun = 0;
    this.blob.frenziesThisRun = 0;
    this.blob.shapesThisRun = [];
    this.blob.magnetsThisRun = 0;
    this.blob.doublesThisRun = 0;
    this.persist();
  }

  bumpPerfect(): void {
    this.blob.perfectsTotal += 1;
    this.blob.perfectsThisRun += 1;
    this.persist();
    this.onStatBumped.emit({
      kind: "perfect",
      delta: 1,
      value: this.blob.perfectsThisRun,
    });
  }

  bumpSave(): void {
    this.blob.savesTotal += 1;
    this.blob.savesThisRun += 1;
    this.persist();
    this.onStatBumped.emit({
      kind: "save",
      delta: 1,
      value: this.blob.savesTotal,
    });
  }

  bumpBoss(bossId: string): void {
    this.blob.bossesTotal += 1;
    this.blob.bossesThisRun += 1;
    if (!this.blob.bossesById.includes(bossId)) {
      this.blob.bossesById.push(bossId);
    }
    this.persist();
    this.onStatBumped.emit({
      kind: "boss",
      delta: 1,
      value: this.blob.bossesTotal,
      bossId,
    });
  }

  bumpFrenzy(): void {
    this.blob.frenziesTotal += 1;
    this.blob.frenziesThisRun += 1;
    this.persist();
    this.onStatBumped.emit({
      kind: "frenzy",
      delta: 1,
      value: this.blob.frenziesThisRun,
    });
  }

  // Combo tracking is "max-watermark" rather than "increment". We only
  // emit an event when the watermark actually grows so achievements
  // don't fire on every comboChange tick.
  noteCombo(combo: number, perfectStreak: number): void {
    let dirty = false;
    if (combo > this.blob.longestCombo) {
      this.blob.longestCombo = combo;
      dirty = true;
    }
    if (perfectStreak > this.blob.longestPerfectStreak) {
      this.blob.longestPerfectStreak = perfectStreak;
      dirty = true;
    }
    if (!dirty) return;
    this.persist();
    this.onStatBumped.emit({
      kind: "comboReached",
      value: this.blob.longestCombo,
    });
  }

  noteShapeVisited(shape: Shape): void {
    if (this.blob.shapesThisRun.includes(shape)) return;
    this.blob.shapesThisRun.push(shape);
    this.persist();
    this.onStatBumped.emit({
      kind: "shapeVisited",
      value: this.blob.shapesThisRun.length,
      shape,
    });
  }

  // PickupManager.onPickupConsumed routes magnet/double consumes here.
  // Per-run counter drives the "magnetised" achievement (5 magnets in
  // one run); lifetime total powers the StatsScreen flex line.
  bumpMagnet(): void {
    this.blob.magnetsTotal += 1;
    this.blob.magnetsThisRun += 1;
    this.persist();
    this.onStatBumped.emit({
      kind: "magnet",
      delta: 1,
      value: this.blob.magnetsThisRun,
    });
  }

  bumpDouble(): void {
    this.blob.doublesTotal += 1;
    this.blob.doublesThisRun += 1;
    this.persist();
    this.onStatBumped.emit({
      kind: "double",
      delta: 1,
      value: this.blob.doublesThisRun,
    });
  }

  // Idempotent per UTC date: starting two daily runs on the same day
  // only counts once. Drives the "daily_7" achievement which rewards
  // the player for showing up across different calendar days.
  noteDailyPlayed(dailyKey: string): void {
    if (this.blob.dailyKeysPlayed.includes(dailyKey)) return;
    this.blob.dailyKeysPlayed.push(dailyKey);
    this.persist();
    this.onStatBumped.emit({
      kind: "dailyPlayed",
      value: this.blob.dailyKeysPlayed.length,
    });
  }

  // Called from MorphHexGame.onDeath / onSuccess after the
  // mode-specific best-score submission, so we capture the run
  // regardless of how it ended. The optional `difficulty` field lets
  // achievements key on the run's difficulty (hard_runner needs to
  // know "this run finished on Hard with distance >= 600").
  recordRunEnded(payload: {
    distance: number;
    score: number;
    combo: number;
    perfectStreak: number;
    difficulty?: string;
  }): void {
    this.blob.runs += 1;
    this.blob.distanceTotal += Math.max(0, Math.round(payload.distance));
    this.blob.scoreTotal += Math.max(0, Math.round(payload.score));
    if (payload.combo > this.blob.longestCombo) {
      this.blob.longestCombo = payload.combo;
    }
    if (payload.perfectStreak > this.blob.longestPerfectStreak) {
      this.blob.longestPerfectStreak = payload.perfectStreak;
    }
    this.persist();
    this.onStatBumped.emit({
      kind: "runEnded",
      value: this.blob.runs,
      delta: Math.max(0, Math.round(payload.distance)),
      difficulty: payload.difficulty,
    });
  }

  // Snapshot for the StatsScreen + achievement evaluation. Returns a
  // shallow copy so the caller can mutate without polluting our blob.
  getAll(): StatsBlob {
    return {
      ...this.blob,
      bossesById: [...this.blob.bossesById],
      dailyKeysPlayed: [...this.blob.dailyKeysPlayed],
      shapesThisRun: [...this.blob.shapesThisRun],
    };
  }

  // Test-only / "reset progress" hook. Wipes the storage but keeps
  // the in-memory blob at zero so the rest of the system behaves
  // sensibly without a reload.
  clear(): void {
    this.blob = { ...EMPTY };
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore quota / privacy errors
    }
  }

  private load(): StatsBlob {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...EMPTY };
      const parsed = JSON.parse(raw) as Partial<StatsBlob> | null;
      if (!parsed || typeof parsed !== "object") return { ...EMPTY };
      // Defensive merge so a future schema bump doesn't crash on the
      // first load -- missing fields fall back to defaults.
      return {
        ...EMPTY,
        ...parsed,
        bossesById: Array.isArray(parsed.bossesById)
          ? [...parsed.bossesById]
          : [],
        dailyKeysPlayed: Array.isArray(parsed.dailyKeysPlayed)
          ? [...parsed.dailyKeysPlayed]
          : [],
        shapesThisRun: Array.isArray(parsed.shapesThisRun)
          ? [...parsed.shapesThisRun]
          : [],
      };
    } catch {
      return { ...EMPTY };
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.blob));
    } catch {
      // private mode / disabled storage; we silently keep going on the
      // in-memory blob.
    }
  }
}
